// Designer's spec units. Scale every layer the same; never use PNG sizes.

export const QUADRANT = { width: 230, height: 527 } as const;

export const TILE_SIZE = 60;
export const TILE_GAP = 2;
export const ROW_PITCH = TILE_SIZE + TILE_GAP; // 62
export const FIRST_ROW_Y = 135;
export const ROWS = 6;

export const COLUMN_X = {
  leftDecorative: 23,
  centre: 85,
  right: 147,
} as const;

// Twelve cells: down the right, across the bottom, up the centre.
export const CELL_COUNT = 12;
const BOTTOM_ROW_Y = FIRST_ROW_Y + (ROWS - 1) * ROW_PITCH; // 445

export interface BoardCell {
  cell: number;
  x: number;
  y: number;
  width: number;
  height: number;
  // Cells 1-7 stay ivory; 8-12 take the player's colour.
  ivory: boolean;
}

export const CELLS: readonly BoardCell[] = Array.from(
  { length: CELL_COUNT },
  (_, i) => {
    const cell = i + 1;
    const goingDown = cell <= ROWS;
    return {
      cell,
      x: goingDown ? COLUMN_X.right : COLUMN_X.centre,
      y: goingDown
        ? FIRST_ROW_Y + (cell - 1) * ROW_PITCH
        : BOTTOM_ROW_Y - (cell - ROWS - 1) * ROW_PITCH,
      width: TILE_SIZE,
      height: TILE_SIZE,
      ivory: cell <= 7,
    };
  }
);

// Decorative lane on the left, drawn at 20% and never walked on.
export const LEFT_LANE_X = COLUMN_X.leftDecorative;

// Layers above the tile grid, in the same units.
export const LAYERS = {
  triangle: { x: 0, y: 0, width: 230, height: 124 },
  tileBox: { x: 0, y: 101, width: 230, height: 426 },
  finishLine: { x: 85, y: 126, width: 60, height: 7 },
  crown: { x: 68, y: 21, width: 94, height: 80 },
  finishRewardText: { x: 78, y: 64, width: 75, height: 32 },
  // Sits just outside the right column, level with cell 1.
  startLabel: { x: 238, y: 158, width: 60, height: 12 },
} as const;

// Power-up inset. The free-roll marker clears the turn arrow.
export const OVERLAY_IN_TILE = {
  standard: { x: 6, y: 6, width: 48, height: 48 },
  freeRollOnTurnTile: { x: 9, y: 17, width: 42, height: 42 },
} as const;

export const ENTRY_ARROW = {
  cell: 1,
  inTile: { x: 15, y: 15, width: 30, height: 30 },
} as const;

export const TURN_ARROW = {
  cell: 7,
  inTile: { x: 23, y: 3, width: 14, height: 14 },
} as const;

export const OPACITY = {
  preparingAllLanes: 1,
  zoomLeftLane: 0.2,
  zoomCentreAndRightLanes: 1,
} as const;

// Full-board intro, before the zoom to the player's lane.
export const FULL_BOARD = {
  width: 1079,
  height: 1079,
  startingArea: 400,
} as const;

// Spec says 400/900ms; both snap on screen. Designer to confirm.
export const TRANSITION = {
  preparingHoldMs: 700,
  zoomMs: 1400,
  readyHandoffMs: 16,
} as const;

// The server counts from 0; the board draws one cell further along.
export function cellForPosition(position: number): number {
  return position + 1;
}

// Position 12 is the finish. It is the crown, not a cell.
export const FINISH_POSITION = 12;

// Server's finishMultiplier. specificGameConfig is null on dev.
export const FINISH_MULTIPLIER = 6;

export function isFinish(position: number): boolean {
  return position >= FINISH_POSITION;
}

// Uniform scale to map these units onto a rendered board width.
export function boardScale(renderedWidth: number): number {
  return renderedWidth / QUADRANT.width;
}

// The ghost board bakes in its 20% opacity. Don't dim it again.
export function ghostPlacement(board: {
  x: number;
  y: number;
  width: number;
}): { x: number; y: number; width: number } {
  const scale = boardScale(board.width);
  return {
    x: board.x - 425 * scale,
    y: board.y - 552 * scale,
    width: FULL_BOARD.width * scale,
  };
}
